import { z } from "zod";
import { Constraint } from "./constraint.js";
import { IngredientGroup } from "./ingredient-group.js";
import { Instruction } from "./instruction.js";
import { MeasurementVocabulary } from "./measurement-vocabulary.js";
import { ModeDefinition } from "./mode.js";
import { OptionCategory } from "./option-category.js";
import { PrintMode } from "./print-mode.js";
import { Widget } from "./widget.js";

// isolatedDeclarations: define output shape as TS interface, annotate
// Zod const with z.ZodType<Interface> so tsc can emit declarations without
// reading across module boundaries (D-15 paranoid-strict tsconfig).
//
// exactOptionalPropertyTypes: optional fields use `T | undefined` to match
// ZodOptional's output type. Omit the key entirely for absent fields; never
// pass `{description: undefined}` — that is a type error under exactOptionalPropertyTypes.
//
// Phase 01.1 changes (Plan 06):
//   - Renamed `widgetInstances` → `widgets` (D-10, D-13, D-16)
//   - Added `modes?: ModeDefinition[] | undefined` (D-13)
//   - Added `optionCategories?: OptionCategory[] | undefined` (D-16)
//   - Added `widgetSlots?: SlotDeclaration[] | undefined` (D-10)

// SlotDeclaration — inline sub-shape for author-declared widget slots.
// slug: kebab-case identifier unique within a recipe's widgetSlots[].
// zone: which layout zone this slot lives in ("options" or "main").
// D-06: "controls" is engine-only and must NEVER appear as a zone here.
export interface SlotDeclaration {
  slug: string;
  zone: "options" | "main";
}

const _slotDeclarationSchema: z.ZodType<SlotDeclaration> = z.strictObject({
  slug: z
    .string()
    .min(1)
    .max(50)
    .regex(/^[a-z][a-z0-9-]*$/, "kebab-case slug required"),
  zone: z.enum(["options", "main"]),
});

export const SlotDeclaration: z.ZodType<SlotDeclaration> = _slotDeclarationSchema;

// VariantOverrideShape — inline sub-shape declaring, per override-field name,
// how that field's value renders. Discriminated over `kind`:
//   - "scalar": the value renders as a plain label+value (no axis resolution).
//   - "size-indexed": the value is a positional array indexed by a scaling
//     widget's size axis; the renderer reads `scalingWidgetId` to find the
//     sibling scaling-matrix widget and `sizeAxisId` to resolve the selected
//     step position, then indexes the override array at that position.
// 11.6 D1 minimal override-shape binding (validation-only + render-hint).
// This is NOT the deferred heavyweight recipe-root walk (the version that
// cross-checks every overrideFields key has a declared shape + that the
// referenced widget/axis ids exist). The minimal binding ships the descriptor
// field only; no cross-reference walk is performed here.
//
// Defense-in-depth entry cap on the variantOverrideShapes record (T-11.6-01-02):
// mirrors the recipe-root array caps (widgets.max(50), constraints.max(100)).
const MAX_OVERRIDE_SHAPE_ENTRIES = 100;

export type VariantOverrideShape =
  | { kind: "scalar" }
  | { kind: "size-indexed"; scalingWidgetId: string; sizeAxisId: string };

const _variantOverrideShapeSchema: z.ZodType<VariantOverrideShape> = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("scalar") }),
  z.strictObject({
    kind: z.literal("size-indexed"),
    scalingWidgetId: z.string().min(1).max(100),
    sizeAxisId: z.string().min(1).max(100),
  }),
]);

export const VariantOverrideShape: z.ZodType<VariantOverrideShape> = _variantOverrideShapeSchema;

export interface RecipeDefinitionV1 {
  schemaVersion: 1; // z.literal(1) — OQ-1: INTEGER not string
  title: string;
  description?: string | undefined;
  servings?: string | undefined;
  yield?: string | undefined;
  imageUrl?: string | undefined;
  prepTimeMinutes?: number | undefined;
  cookTimeMinutes?: number | undefined;
  ingredientGroups: IngredientGroup[];
  instructions: Instruction[];
  widgets?: Widget[] | undefined;
  constraints?: Constraint[] | undefined;
  printModes?: PrintMode[] | undefined;
  modes?: ModeDefinition[] | undefined;
  optionCategories?: OptionCategory[] | undefined;
  widgetSlots?: SlotDeclaration[] | undefined;
  variantOverrideShapes?: Record<string, VariantOverrideShape> | undefined;
  measurementVocabulary?: MeasurementVocabulary | undefined;
}

// D-08 in-body fields: schemaVersion lives IN the body for file-mode self-description.
// D-09 out-of-band (id, slug, ownerId, createdAt, updatedAt) are Phase 2 sidecar — NOT here.
// D-10 strictness: z.strictObject rejects unknown keys at the top level.
// Defense-in-depth array bounds (T-01-05-08 + plan decisions):
//   widgets.max(50), constraints.max(100), printModes.max(10), modes.max(10),
//   optionCategories.max(50), widgetSlots.max(20)
const _recipeSchema = z.strictObject({
  schemaVersion: z.literal(1), // OQ-1: INTEGER 1, not string "v1" (Pitfall 2 mitigation)
  title: z.string().min(1).max(200),
  description: z.string().min(1).max(2000).optional(),
  servings: z.string().min(1).max(100).optional(),
  yield: z.string().min(1).max(100).optional(),
  imageUrl: z.string().url().optional(),
  prepTimeMinutes: z.number().int().positive().optional(),
  cookTimeMinutes: z.number().int().positive().optional(),
  ingredientGroups: z.array(IngredientGroup).min(1), // D-07 always grouped; ≥1 group required
  instructions: z.array(Instruction).min(1), // D-05 floor minimum
  widgets: z.array(Widget).max(50).optional(), // defense-in-depth; renamed from widgetInstances (D-13)
  constraints: z.array(Constraint).max(100).optional(), // defense-in-depth
  printModes: z.array(PrintMode).max(10).optional(), // defense-in-depth
  modes: z.array(ModeDefinition).max(10).optional(), // D-13 recipe-declared modes
  optionCategories: z.array(OptionCategory).max(50).optional(), // D-16 option categories
  widgetSlots: z.array(SlotDeclaration).max(20).optional(), // D-10 author-declared slots
  variantOverrideShapes: z
    .record(z.string().min(1).max(100), _variantOverrideShapeSchema)
    .refine((rec) => Object.keys(rec).length <= MAX_OVERRIDE_SHAPE_ENTRIES, {
      message: `variantOverrideShapes may declare at most ${MAX_OVERRIDE_SHAPE_ENTRIES} fields`,
    })
    .optional(), // 11.6 D1 minimal override-shape binding (render-hint; validation-only — NOT the deferred heavyweight recipe-root walk)
  measurementVocabulary: MeasurementVocabulary.optional(), // 11.8 Strand-2 carrier: recipe-declared measurement vocabulary DATA (render-only; no meta-schema unit type; sub-schema carries its own DoS bounds)
});

export const RecipeDefinitionV1: z.ZodType<RecipeDefinitionV1> = _recipeSchema;
